import style from './ContactForm.module.scss';

import type { FormikErrors, FormikTouched } from 'formik';
import React, { useState } from 'react';
import Recaptcha from 'reaptcha';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import { FormFields } from 'api/contentful/contentTypes.cloverdotcom';
import { Environment } from 'utils/Environment';

import { ValidationError } from '../Form/ValidationError';
import type { ContactInfo } from './ContactFormBody';
import { MaskedInput } from './MaskedFormField';
import { FIELDS_TEST_ID } from './Fields.const';

type ContactFormField = {
  generateInputProps: any;
  touched: any;
  errors: any;
  setFieldValue?: any;
  disabled?: boolean;
};

type ContactFormFieldWithValues = ContactFormField & {
  values: any;
};

type ContactFormDropdownField = ContactFormFieldWithValues & {
  options: any;
  valueKey: string;
  formFieldLabel: string;
};

export const STATE_LIST = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

const MaskedPhoneInput = MaskedInput('(###) ###-####', Input);
const Masked4DigitInput = MaskedInput('####', Input);

const ErrorComponent: React.FC<{
  valueKey: string;
  touched: FormikTouched<ContactInfo>;
  errors: FormikErrors<ContactInfo>;
}> = ({ valueKey, touched, errors }) => {
  if (!touched[valueKey]) return null;

  return errors[valueKey] ? <ValidationError message={errors[valueKey]} /> : null;
};

export const BusinessNameField: React.FC<ContactFormField> = ({ generateInputProps, touched, errors }) => (
  <Grid item xs={12} lg={12} classes={{ item: style.gridInput }} key="businessName">
    <FormControl fullWidth>
      <InputLabel htmlFor="businessName">{FormFields.BUSINESS_NAME}</InputLabel>
      <Input
        data-testid={FIELDS_TEST_ID.businessName}
        autoComplete="organization"
        {...generateInputProps('businessName')}
      />
      <ErrorComponent valueKey="businessName" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const BusinessAddressLine1Field: React.FC<ContactFormField> = ({
  generateInputProps,
  touched,
  errors,
}) => (
  <Grid
    item
    xs={12}
    sm={12}
    md={12}
    lg={12}
    xl={12}
    classes={{ item: style.gridInput }}
    key="businessAddressLine1"
  >
    <FormControl fullWidth>
      <InputLabel htmlFor="businessAddressLine1">Business Address</InputLabel>
      <Input
        autoComplete="address-line1"
        inputProps={{ maxLength: 24 }}
        {...generateInputProps('businessAddressLine1')}
      />
      <ErrorComponent valueKey="businessAddressLine1" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const AddressLine2Field: React.FC<ContactFormField> = ({ generateInputProps, touched, errors }) => (
  <Grid item xs={12} sm={12} md={12} lg={12} xl={12} classes={{ item: style.gridInput }} key="addressLine2">
    <FormControl fullWidth>
      <InputLabel htmlFor="addressLine2">Suite, unit, building, floor, etc. (optional)</InputLabel>
      <Input
        autoComplete="address-line2"
        inputProps={{ maxLength: 24 }}
        {...generateInputProps('addressLine2')}
      />
      <ErrorComponent valueKey="addressLine2" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const CityField: React.FC<ContactFormField> = ({ generateInputProps, touched, errors }) => (
  <Grid item xs={12} sm={12} md={12} lg={4} xl={4} classes={{ item: style.gridInput }} key="city">
    <FormControl fullWidth>
      <InputLabel htmlFor="city">City</InputLabel>
      <Input autoComplete="address-level2" {...generateInputProps('city')} />
      <ErrorComponent valueKey="city" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const StateField: React.FC<ContactFormFieldWithValues> = ({
  generateInputProps,
  touched,
  errors,
  values,
}) => {
  const key = 'state';
  return (
    <Grid item xs={12} sm={12} md={12} lg={4} xl={4} classes={{ item: style.gridInput }} key={key}>
      <FormControl fullWidth>
        <InputLabel htmlFor="state">State</InputLabel>
        <Select autoComplete="address-level1" {...generateInputProps('state')} value={values[key]}>
          {STATE_LIST.map(s => (
            <MenuItem value={s} key={`${key}-${s}`}>
              {s}
            </MenuItem>
          ))}
        </Select>
        <ErrorComponent valueKey="state" touched={touched} errors={errors} />
      </FormControl>
    </Grid>
  );
};

export const ZipCodeField: React.FC<ContactFormField> = ({ generateInputProps, touched, errors }) => (
  <Grid item xs={12} sm={12} md={12} lg={12} xl={12} classes={{ item: style.gridInput }} key="zipCode">
    <FormControl fullWidth>
      <InputLabel htmlFor="zipCode">Zip Code</InputLabel>
      <Input
        data-testid={FIELDS_TEST_ID.zipCode}
        inputProps={{ maxLength: 5 }}
        autoComplete="postal-code"
        {...generateInputProps('zipCode')}
      />
      <ErrorComponent valueKey="zipCode" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const EmailField: React.FC<ContactFormField> = ({ generateInputProps, touched, errors, disabled }) => (
  <Grid item xs={12} lg={12} classes={{ item: style.gridInput }} key="emailAddress">
    <FormControl fullWidth>
      <InputLabel htmlFor="emailAddress">{FormFields.EMAIL}</InputLabel>
      <Input
        data-testid={FIELDS_TEST_ID.emailAddress}
        type="email"
        disabled={disabled}
        autoComplete="email"
        {...generateInputProps('emailAddress')}
      />
      <ErrorComponent valueKey="emailAddress" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const FirstNameField: React.FC<ContactFormField> = ({ generateInputProps, touched, errors }) => (
  <Grid item xs={12} sm={12} md={12} lg={6} xl={6} classes={{ item: style.gridInput }} key="firstName">
    <FormControl fullWidth>
      <InputLabel htmlFor="firstName">First Name</InputLabel>
      <Input
        data-testid={FIELDS_TEST_ID.firstName}
        inputProps={{ maxLength: 24 }}
        autoComplete="given-name"
        {...generateInputProps('firstName')}
      />
      <ErrorComponent valueKey="firstName" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const LastNameField: React.FC<ContactFormField> = ({ generateInputProps, touched, errors }) => (
  <Grid item xs={12} sm={12} md={12} lg={6} xl={6} classes={{ item: style.gridInput }} key="lastName">
    <FormControl fullWidth>
      <InputLabel htmlFor="lastName">Last Name</InputLabel>
      <Input
        data-testid={FIELDS_TEST_ID.lastName}
        inputProps={{ maxLength: 24 }}
        autoComplete="family-name"
        {...generateInputProps('lastName')}
      />
      <ErrorComponent valueKey="lastName" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const PhoneNumberField: React.FC<ContactFormField> = ({ generateInputProps, touched, errors }) => {
  const inputProps = generateInputProps('phoneNumber');
  return (
    <Grid item xs={12} lg={12} classes={{ item: style.gridInput }} key="phoneNumber">
      <FormControl fullWidth>
        <InputLabel htmlFor="phoneNumber">{FormFields.PHONE_NUMBER}</InputLabel>
        <MaskedPhoneInput
          type="tel"
          autoComplete="tel"
          input={{ onChange: inputProps.onChange }}
          {...inputProps}
          data-testid={FIELDS_TEST_ID.phoneNumber}
        />
        <ErrorComponent valueKey="phoneNumber" touched={touched} errors={errors} />
      </FormControl>
    </Grid>
  );
};

export const MobilePhoneNumberField: React.FC<ContactFormField> = ({
  generateInputProps,
  touched,
  errors,
}) => {
  const inputProps = generateInputProps('mobilePhoneNumber');
  return (
    <Grid item xs={12} lg={12} classes={{ item: style.gridInput }} key="mobilePhoneNumber">
      <FormControl fullWidth>
        <InputLabel htmlFor="mobilePhoneNumber">Mobile Phone Number</InputLabel>
        <MaskedPhoneInput input={{ onChange: inputProps.onChange }} {...inputProps} />
        <ErrorComponent valueKey="mobilePhoneNumber" touched={touched} errors={errors} />
      </FormControl>
    </Grid>
  );
};

export const SsnLast4Field: React.FC<ContactFormField> = ({ generateInputProps, touched, errors }) => {
  const inputProps = generateInputProps('ssnLast4');
  return (
    <Grid item xs={12} sm={12} md={12} lg={12} xl={12} classes={{ item: style.gridInput }} key="ssnLast4">
      <FormControl fullWidth>
        <InputLabel htmlFor="ssnLast4">Social Security - Last Four Digits</InputLabel>
        <Masked4DigitInput input={{ onChange: inputProps.onChange }} {...inputProps} />
        <ErrorComponent valueKey="ssnLast4" touched={touched} errors={errors} />
      </FormControl>
    </Grid>
  );
};

export const BankAccountLast4Field: React.FC<ContactFormField> = ({
  generateInputProps,
  touched,
  errors,
}) => {
  const inputProps = generateInputProps('bankAccountLast4');
  return (
    <Grid
      item
      xs={12}
      sm={12}
      md={12}
      lg={12}
      xl={12}
      classes={{ item: style.gridInput }}
      key="bankAccountLast4"
    >
      <FormControl fullWidth>
        <InputLabel htmlFor="bankAccountLast4">Bank Account - Last Four Digits</InputLabel>
        <Masked4DigitInput input={{ onChange: inputProps.onChange }} {...inputProps} />
        <ErrorComponent valueKey="bankAccountLast4" touched={touched} errors={errors} />
      </FormControl>
    </Grid>
  );
};

export const MidField: React.FC<ContactFormField> = ({ generateInputProps, touched, errors }) => (
  <Grid item xs={12} sm={12} md={12} lg={12} xl={12} classes={{ item: style.gridInput }} key="mid">
    <FormControl fullWidth>
      <InputLabel htmlFor="mid">{FormFields.MID}</InputLabel>
      <Input inputProps={{ maxLength: 12 }} {...generateInputProps('mid')} />
      <ErrorComponent valueKey="mid" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const DropdownField: React.FC<ContactFormDropdownField> = ({
  generateInputProps,
  touched,
  errors,
  options,
  formFieldLabel,
  valueKey,
  values,
}) => (
  <Grid item xs={12} sm={12} md={12} lg={12} xl={12} classes={{ item: style.gridInput }} key={valueKey}>
    <FormControl fullWidth>
      <InputLabel htmlFor={valueKey}>{formFieldLabel}</InputLabel>
      <Select {...generateInputProps(valueKey)} value={values[valueKey]}>
        {options.map((option: any) => {
          return (
            <MenuItem value={option.value} key={`${formFieldLabel}: ${option.value}`}>
              {option.label}
            </MenuItem>
          );
        })}
      </Select>
      <ErrorComponent valueKey={valueKey || ''} touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);

export const GoogleRecaptcha: React.FC<{ setFieldValue: any }> = ({ setFieldValue }) => {
  const [recaptcha, setRecaptcha] = useState<Recaptcha | null>(null);

  return (
    <Grid item xs={12} classes={{ item: style.gridInput }} key="googleRecaptcha">
      <FormControl fullWidth>
        <Recaptcha
          ref={e => setRecaptcha(e)}
          sitekey={Environment.GOOGLE_RECAPTCHA_SITE_KEY}
          onVerify={response => setFieldValue('googleRecaptcha', response)}
          onRender={() => recaptcha && recaptcha.execute()}
          size="invisible"
          badge="inline"
        />
      </FormControl>
    </Grid>
  );
};

export const RequiredCheckbox: React.FC<ContactFormField> = ({
  generateInputProps,
  touched,
  errors,
  children,
}) => (
  <Grid
    item
    xs={12}
    sm={12}
    md={12}
    lg={12}
    xl={12}
    classes={{ item: style.gridInput }}
    key="requiredCheckbox"
  >
    <FormControl fullWidth>
      <FormControlLabel
        control={
          <Checkbox
            color="default"
            {...generateInputProps('requiredCheckbox')}
            key="requiredCheckbox"
            classes={{ root: style.gridCheckbox }}
          />
        }
        label={children}
      />
      <ErrorComponent valueKey="requiredCheckbox" touched={touched} errors={errors} />
    </FormControl>
  </Grid>
);
